import React, { useState, useEffect } from 'react';
import { Plus, Trash, X, PencilSimple, Sparkle } from '@phosphor-icons/react';
import { EpLabel } from './Shared';
import RememberedGuests from './RememberedGuests';
import SmartFillModal from './SmartFillModal';

const HEALTH_GOAL_OPTIONS = ['Low Oil', 'More Protein', 'Less Sugar', 'Low Carb', 'High Fiber', 'Weight Loss', 'Diabetic Friendly', 'Iron Rich'];

export default function EaterProfilesTab({ api }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const r = await api.get('/meals/eater-profiles');
      setProfiles(r.data || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { fetchProfiles(); /* eslint-disable-next-line */ }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try { await api.post('/meals/eater-profiles/seed-defaults'); await fetchProfiles(); } catch (_) { /* ignore */ }
    setSeeding(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this profile?')) return;
    try { await api.delete(`/meals/eater-profiles/${id}`); await fetchProfiles(); } catch (_) { /* ignore */ }
  };

  if (loading) return <div className="text-center py-12 text-neutral-500 text-sm">Loading profiles…</div>;

  return (
    <div className="space-y-3" data-testid="eater-profiles-tab">
      <div className="glass-card p-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-white">Eater Profiles</h3>
          <p className="text-[11px] text-neutral-500">Plan different dishes for kids, adults, or each family member. AI Chef will respect each profile's preferences.</p>
        </div>
        <div className="flex gap-2">
          {profiles.length === 0 && (
            <button onClick={handleSeed} disabled={seeding} data-testid="seed-defaults-tab-btn"
              className="px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30 text-[11px] font-bold hover:bg-purple-500/25">
              {seeding ? '…' : '+ Add Kids & Adults'}
            </button>
          )}
          <button onClick={() => setEditing('new')} data-testid="add-eater-profile-btn"
            className="px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white text-[11px] font-bold hover:bg-[#ff803a] inline-flex items-center gap-1">
            <Plus size={11} weight="bold" /> Add profile
          </button>
        </div>
      </div>

      {profiles.length === 0 && (
        <div className="glass-card p-10 text-center text-neutral-500 text-sm">
          No eater profiles yet. Add "Kids" + "Adults" group profiles (quick start), or create a profile per family member.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {profiles.map(p => (
          <EaterProfileCard key={p.profile_id} profile={p}
            onEdit={() => setEditing(p)} onDelete={() => handleDelete(p.profile_id)} />
        ))}
      </div>

      {editing && (
        <EaterProfileEditModal api={api} profile={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await fetchProfiles(); }} />
      )}

      <RememberedGuests api={api} />
    </div>
  );
}

function EaterProfileCard({ profile, onEdit, onDelete }) {
  const color = profile.color || '#A855F7';
  return (
    <div className="glass-card p-4 relative" data-testid={`eater-card-${profile.profile_id}`}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
            style={{ background: `${color}20`, border: `1px solid ${color}40` }}>{profile.icon || '🧑'}</div>
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-white truncate">{profile.name}</h4>
            <p className="text-[10px] text-neutral-500 capitalize">
              {profile.kind}{profile.age_years ? ` · ${profile.age_years}y` : ''} · {profile.spice_level}
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} data-testid={`eater-edit-${profile.profile_id}`}
            className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-400 hover:text-white"><PencilSimple size={12} /></button>
          <button onClick={onDelete} data-testid={`eater-delete-${profile.profile_id}`}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-neutral-400 hover:text-red-400"><Trash size={12} /></button>
        </div>
      </div>
      <div className="space-y-1 text-[11px]">
        {profile.diet_type && <p className="text-neutral-300"><span className="text-neutral-500">Diet:</span> {profile.diet_type}</p>}
        <p className="text-neutral-300"><span className="text-neutral-500">Portion:</span> {profile.portion_size}x</p>
        {(profile.liked_dishes || []).length > 0 && (
          <p className="text-emerald-300/80 truncate">♥ {profile.liked_dishes.slice(0, 4).join(', ')}{profile.liked_dishes.length > 4 ? '…' : ''}</p>
        )}
        {(profile.disliked_dishes || []).length > 0 && (
          <p className="text-red-300/80 truncate">✕ {profile.disliked_dishes.slice(0, 4).join(', ')}{profile.disliked_dishes.length > 4 ? '…' : ''}</p>
        )}
        {(profile.allergies || []).length > 0 && (
          <p className="text-amber-300/80 truncate">⚠ Allergic: {profile.allergies.join(', ')}</p>
        )}
        {(profile.health_goals || []).length > 0 && (
          <p className="text-green-300/80 truncate" data-testid={`eater-card-goals-${profile.profile_id}`}>
            ★ Goals: {profile.health_goals.slice(0, 4).join(', ')}{profile.health_goals.length > 4 ? '…' : ''}
          </p>
        )}
      </div>
    </div>
  );
}

function EaterProfileEditModal({ api, profile, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: profile?.name || '',
    kind: profile?.kind || 'individual',
    icon: profile?.icon || '🧑',
    color: profile?.color || '#A855F7',
    age_years: profile?.age_years || '',
    portion_size: profile?.portion_size ?? 1.0,
    spice_level: profile?.spice_level || 'medium',
    diet_type: profile?.diet_type || '',
    allergies: (profile?.allergies || []).join(', '),
    cuisine_preferences: (profile?.cuisine_preferences || []).join(', '),
    health_goals: profile?.health_goals || [],
    liked_dishes: (profile?.liked_dishes || []).join(', '),
    disliked_dishes: (profile?.disliked_dishes || []).join(', '),
    meal_defaults: profile?.meal_defaults || {},
    notes: profile?.notes || '',
    active: profile?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSmart, setShowSmart] = useState(false);
  const parseList = (s) => (s || '').split(/,|\n/).map(x => x.trim()).filter(Boolean);
  const colorChoices = ['#F472B6', '#A855F7', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];
  const iconChoices = ['👶', '👧', '👦', '🧑', '👩', '🧔', '👵', '🐶'];

  const toggleGoal = (g) => {
    setForm(f => ({ ...f, health_goals: f.health_goals.includes(g) ? f.health_goals.filter(x => x !== g) : [...f.health_goals, g] }));
  };

  // Smart-fill: merge parsed fields into the form. Empty/null values are skipped
  // so the user's existing input isn't accidentally wiped.
  const applySmartFill = ({ parsed }) => {
    setForm(f => {
      const next = { ...f };
      if (parsed.name) next.name = parsed.name;
      if (parsed.kind) next.kind = parsed.kind;
      if (parsed.age_years != null) next.age_years = String(parsed.age_years);
      if (parsed.diet_type) next.diet_type = parsed.diet_type;
      if (parsed.spice_level) next.spice_level = parsed.spice_level;
      if (parsed.portion_size != null) next.portion_size = parsed.portion_size;
      // List fields are MERGED (deduped, case-insensitive) so the user keeps existing entries.
      const mergeList = (existing, additions) => {
        const seen = new Set((existing || []).map(s => String(s).toLowerCase().trim()));
        const out = [...(existing || [])];
        (additions || []).forEach(a => {
          const key = String(a).toLowerCase().trim();
          if (key && !seen.has(key)) { seen.add(key); out.push(a); }
        });
        return out;
      };
      if (parsed.liked_dishes?.length) {
        next.liked_dishes = mergeList(parseList(f.liked_dishes), parsed.liked_dishes).join(', ');
      }
      if (parsed.disliked_dishes?.length) {
        next.disliked_dishes = mergeList(parseList(f.disliked_dishes), parsed.disliked_dishes).join(', ');
      }
      if (parsed.allergies?.length) {
        next.allergies = mergeList(parseList(f.allergies), parsed.allergies).join(', ');
      }
      if (parsed.cuisine_preferences?.length) {
        next.cuisine_preferences = mergeList(parseList(f.cuisine_preferences), parsed.cuisine_preferences).join(', ');
      }
      if (parsed.health_goals?.length) {
        next.health_goals = mergeList(f.health_goals, parsed.health_goals);
      }
      if (parsed.notes) {
        next.notes = f.notes ? `${f.notes}\n${parsed.notes}` : parsed.notes;
      }
      return next;
    });
    setShowSmart(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    const payload = {
      name: form.name, kind: form.kind, icon: form.icon, color: form.color,
      age_years: form.age_years ? Number(form.age_years) : null,
      portion_size: Number(form.portion_size),
      spice_level: form.spice_level, diet_type: form.diet_type,
      allergies: parseList(form.allergies),
      cuisine_preferences: parseList(form.cuisine_preferences),
      health_goals: form.health_goals || [],
      liked_dishes: parseList(form.liked_dishes),
      disliked_dishes: parseList(form.disliked_dishes),
      meal_defaults: form.meal_defaults || {},
      notes: form.notes, active: form.active,
    };
    try {
      if (profile) await api.put(`/meals/eater-profiles/${profile.profile_id}`, payload);
      else await api.post('/meals/eater-profiles', payload);
      onSaved && onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="eater-edit-modal">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">{profile ? `Edit · ${profile.name}` : 'New Eater Profile'}</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSmart(true)} data-testid="ep-smart-fill-btn"
              className="px-2.5 py-1 rounded-lg text-[11px] font-bold inline-flex items-center gap-1"
              style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.20), rgba(236,72,153,0.20))', color: '#E9D5FF', border: '1px solid rgba(168,85,247,0.40)' }}>
              <Sparkle size={11} weight="fill" /> Smart fill
            </button>
            <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <EpLabel>Name</EpLabel>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="ep-name" className="ep-input" placeholder="e.g., Aanya, Kids, Wife" />
            </div>
            <div>
              <EpLabel>Type</EpLabel>
              <div className="flex gap-1.5">
                {[['individual', 'Person'], ['group', 'Group']].map(([id, lbl]) => (
                  <button key={id} type="button" onClick={() => setForm(f => ({ ...f, kind: id }))}
                    className={`flex-1 py-2 rounded-lg text-[11px] font-bold ${form.kind === id ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <EpLabel>Avatar &amp; color</EpLabel>
            <div className="flex items-center gap-2 flex-wrap">
              {iconChoices.map(ic => (
                <button key={ic} type="button" onClick={() => setForm(f => ({ ...f, icon: ic }))}
                  className={`w-9 h-9 rounded-lg text-base ${form.icon === ic ? 'bg-purple-500/20 ring-2 ring-purple-400' : 'bg-white/5 hover:bg-white/10'}`}>{ic}</button>
              ))}
              <div className="w-px h-6 bg-white/10 mx-1" />
              {colorChoices.map(c => (
                <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                  className={`w-7 h-7 rounded-full ${form.color === c ? 'ring-2 ring-white/60' : ''}`} style={{ background: c }} />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <EpLabel>Age (yrs)</EpLabel>
              <input type="number" min="0" max="120" step="0.5" value={form.age_years}
                onChange={e => setForm(f => ({ ...f, age_years: e.target.value }))} data-testid="ep-age" className="ep-input" />
            </div>
            <div>
              <EpLabel>Portion size ({form.portion_size}x)</EpLabel>
              <input type="range" min="0.25" max="2" step="0.25" value={form.portion_size}
                onChange={e => setForm(f => ({ ...f, portion_size: e.target.value }))} data-testid="ep-portion" className="w-full accent-purple-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <EpLabel>Spice level</EpLabel>
              <select value={form.spice_level} onChange={e => setForm(f => ({ ...f, spice_level: e.target.value }))} data-testid="ep-spice" className="ep-input">
                <option value="mild" style={{ background: '#0a0a0a' }}>Mild</option>
                <option value="medium" style={{ background: '#0a0a0a' }}>Medium</option>
                <option value="hot" style={{ background: '#0a0a0a' }}>Hot</option>
                <option value="extra_hot" style={{ background: '#0a0a0a' }}>Extra hot</option>
              </select>
            </div>
            <div>
              <EpLabel>Diet type</EpLabel>
              <input value={form.diet_type} onChange={e => setForm(f => ({ ...f, diet_type: e.target.value }))}
                data-testid="ep-diet" className="ep-input" placeholder="e.g., Jain Veg, Eggetarian" />
            </div>
          </div>
          <div>
            <EpLabel>Liked dishes <span className="text-[9px] text-neutral-500">comma-separated</span></EpLabel>
            <textarea rows="2" value={form.liked_dishes} onChange={e => setForm(f => ({ ...f, liked_dishes: e.target.value }))}
              data-testid="ep-liked" className="ep-input" placeholder="Idli, Dosa, Paratha" />
          </div>
          <div>
            <EpLabel>Disliked dishes</EpLabel>
            <textarea rows="2" value={form.disliked_dishes} onChange={e => setForm(f => ({ ...f, disliked_dishes: e.target.value }))}
              data-testid="ep-disliked" className="ep-input" placeholder="Bhindi, Karela" />
          </div>
          <div>
            <EpLabel>Allergies</EpLabel>
            <input value={form.allergies} onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))}
              data-testid="ep-allergies" className="ep-input" placeholder="peanuts, eggs" />
          </div>
          <div>
            <EpLabel>Cuisine preferences</EpLabel>
            <input value={form.cuisine_preferences} onChange={e => setForm(f => ({ ...f, cuisine_preferences: e.target.value }))}
              data-testid="ep-cuisines" className="ep-input" placeholder="North Indian, South Indian, Italian" />
          </div>
          <div>
            <EpLabel>Health goals <span className="text-[9px] text-neutral-500">overrides family default</span></EpLabel>
            <div className="flex flex-wrap gap-1.5" data-testid="ep-health-goals">
              {HEALTH_GOAL_OPTIONS.map(g => {
                const on = form.health_goals.includes(g);
                return (
                  <button key={g} type="button" onClick={() => toggleGoal(g)}
                    data-testid={`ep-goal-${g.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${on ? 'bg-green-500/20 text-green-300 border-green-500/40' : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                    {on && '✓ '}{g}
                  </button>
                );
              })}
            </div>
            {form.health_goals.length === 0 && (
              <p className="text-[10px] text-neutral-500 mt-1 italic">No overrides — uses family-wide health goals from Food Profile.</p>
            )}
          </div>
          <div>
            <EpLabel>Meal defaults <span className="text-[9px] text-neutral-500">(what they usually eat)</span></EpLabel>
            <div className="grid grid-cols-2 gap-2">
              {['breakfast', 'lunch', 'dinner', 'snacks'].map(slot => (
                <input key={slot} value={form.meal_defaults?.[slot] || ''}
                  onChange={e => setForm(f => ({ ...f, meal_defaults: { ...f.meal_defaults, [slot]: e.target.value } }))}
                  data-testid={`ep-default-${slot}`} className="ep-input" placeholder={`${slot}…`} />
              ))}
            </div>
          </div>
          <div>
            <EpLabel>Notes</EpLabel>
            <textarea rows="2" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="ep-notes" className="ep-input" placeholder="e.g., Loves creamy textures, hates onions" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
              className="accent-purple-500" />
            <span className="text-xs text-neutral-300">Active — include in AI Chef planning</span>
          </label>
        </div>
        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="ep-save"
            className="px-4 py-2 rounded-lg bg-purple-500 text-white text-xs font-bold hover:bg-purple-400 disabled:opacity-50">
            {saving ? 'Saving…' : (profile ? 'Save changes' : 'Create profile')}
          </button>
        </div>
        <style>{`.ep-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:12px;color:#fff;outline:none}.ep-input:focus{border-color:#A855F7}`}</style>
      </div>
      {showSmart && (
        <SmartFillModal api={api} target="eater_profile"
          title="Smart fill — Eater profile"
          placeholder="e.g., Hrishaa is 11, vegetarian, mild spice, loves shushi, dimsum, frankie, hates karela, needs iron and more protein"
          examples={[
            "Hrishaa, 11 yrs, veg, mild spice, loves shushi/dimsum/frankie, hates karela, needs iron",
            "Apeksha, 41, vegetarian, hot spice, loves dosa/vada pav/kanda bhajia, avoid garlic, wants weight loss",
            "Dira, 5, Jain veg, mild, half portion, loves idli/dosa/pizza/frankie",
          ]}
          onApply={applySmartFill}
          onClose={() => setShowSmart(false)} />
      )}
    </div>
  );
}
